// hooks/useMobileOptimization.js
import { useState, useEffect, useRef } from 'react';
import MobileOptimizer from '../utils/mobileOptimizer';

/**
 * Hook for mobile-specific optimizations
 */
export function useMobileOptimization() {
  const [isMobile, setIsMobile] = useState(MobileOptimizer.isMobile);
  const [isLowEndDevice, setIsLowEndDevice] = useState(MobileOptimizer.isLowEndDevice);
  const [connectionType, setConnectionType] = useState(MobileOptimizer.connectionType);
  const [optimizedSettings, setOptimizedSettings] = useState(() => 
    MobileOptimizer.getOptimizedSettings()
  );

  useEffect(() => {
    // Update settings when connection changes
    const updateSettings = () => {
      setConnectionType(MobileOptimizer.connectionType);
      setOptimizedSettings(MobileOptimizer.getOptimizedSettings());
    };

    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', updateSettings);
      return () => navigator.connection.removeEventListener('change', updateSettings);
    }
  }, []);

  return {
    isMobile,
    isLowEndDevice,
    connectionType,
    optimizedSettings,
    shouldEnableFeature: MobileOptimizer.shouldEnableFeature.bind(MobileOptimizer)
  };
}

/**
 * Hook for optimizing component rendering on mobile
 */
export function useMobileComponentOptimization(componentName) {
  const componentRef = useRef(null);
  const { isMobile, isLowEndDevice, optimizedSettings } = useMobileOptimization();

  useEffect(() => {
    if (componentRef.current) {
      MobileOptimizer.optimizeComponent(componentRef);
    }
  }, []);

  // Optimize render frequency on low-end devices
  const shouldSkipRender = useRef(false);
  const lastRenderTime = useRef(Date.now());

  useEffect(() => {
    if (isLowEndDevice) {
      const now = Date.now();
      const timeSinceLastRender = now - lastRenderTime.current;
      
      // Skip renders if they're happening too frequently (< 16ms for 60fps)
      if (timeSinceLastRender < 32) { // 30fps for low-end devices
        shouldSkipRender.current = true;
        return;
      }
      
      lastRenderTime.current = now;
      shouldSkipRender.current = false;
    }
  });

  return {
    componentRef,
    shouldSkipRender: shouldSkipRender.current,
    optimizedSettings,
    isMobile,
    isLowEndDevice
  };
}

/**
 * Hook for mobile-optimized data loading
 */
export function useMobileDataLoading(dataLoader, dependencies = []) {
  const { optimizedSettings, isLowEndDevice } = useMobileOptimization();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadData = async (options = {}) => {
    setLoading(true);
    setError(null);

    try {
      // Apply mobile-specific options
      const mobileOptions = {
        ...options,
        timeout: optimizedSettings.requestTimeout,
        batchSize: optimizedSettings.batchSize,
        concurrent: optimizedSettings.maxConcurrentRequests
      };

      const result = await dataLoader(mobileOptions);
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, dependencies);

  return {
    data,
    loading,
    error,
    reload: loadData,
    optimizedSettings
  };
}

/**
 * Hook for mobile-optimized image loading
 */
export function useMobileImageOptimization() {
  const { optimizedSettings, connectionType } = useMobileOptimization();

  const getOptimizedImageProps = (src, alt) => {
    const props = {
      src,
      alt,
      loading: 'lazy',
      decoding: 'async'
    };

    // Adjust image quality based on connection
    if (connectionType === '2g' || connectionType === 'slow-2g') {
      // Use lower quality images for slow connections
      props.style = { 
        filter: `contrast(${optimizedSettings.imageQuality})`,
        imageRendering: 'optimizeSpeed'
      };
    }

    // Add intersection observer threshold
    props['data-threshold'] = optimizedSettings.lazyLoadThreshold;

    return props;
  };

  return {
    getOptimizedImageProps,
    imageQuality: optimizedSettings.imageQuality,
    lazyLoadThreshold: optimizedSettings.lazyLoadThreshold
  };
}

/**
 * Hook for mobile touch optimizations
 */
export function useMobileTouchOptimization() {
  const { isMobile } = useMobileOptimization();
  const [touchState, setTouchState] = useState({
    isTouch: false,
    touchStart: null,
    touchEnd: null
  });

  useEffect(() => {
    if (!isMobile) return;

    const handleTouchStart = (e) => {
      setTouchState(prev => ({
        ...prev,
        isTouch: true,
        touchStart: {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          time: Date.now()
        }
      }));
    };

    const handleTouchEnd = (e) => {
      setTouchState(prev => ({
        ...prev,
        isTouch: false,
        touchEnd: {
          x: e.changedTouches[0].clientX,
          y: e.changedTouches[0].clientY,
          time: Date.now()
        }
      }));
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile]);

  const getTouchGesture = () => {
    const { touchStart, touchEnd } = touchState;
    if (!touchStart || !touchEnd) return null;

    const deltaX = touchEnd.x - touchStart.x;
    const deltaY = touchEnd.y - touchStart.y;
    const deltaTime = touchEnd.time - touchStart.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Detect swipe gestures
    if (distance > 50 && deltaTime < 300) {
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return deltaX > 0 ? 'swipeRight' : 'swipeLeft';
      } else {
        return deltaY > 0 ? 'swipeDown' : 'swipeUp';
      }
    }

    // Detect tap
    if (distance < 10 && deltaTime < 200) {
      return 'tap';
    }

    return null;
  };

  return {
    touchState,
    getTouchGesture,
    isMobile
  };
}

export default useMobileOptimization;